import { Suspense, useEffect, useState, type ReactNode } from 'react';
import { Outlet } from 'react-router';
import { RouteFallback } from '@/components/RouteFallback';

/**
 * Hold the skeleton until the lazy chunk has been pending past `ms`.
 * Fast / already-warmed navigations never flash a blank pulse frame.
 */
function DelayedFallback({
  ms = 160,
  children,
}: {
  ms?: number;
  children: ReactNode;
}) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setShow(true), ms);
    return () => window.clearTimeout(id);
  }, [ms]);
  if (!show) {
    return <div className="min-h-[50vh] w-full" aria-hidden />;
  }
  return children;
}

/**
 * Instant page handoff (native-app feel).
 *
 * BrowserRouter transitions keep the previous page painted until the next
 * lazy chunk is ready — no fade, no slide, no overlap. Prefetched tab chunks
 * make most switches commit immediately.
 */
export function AnimatedOutlet() {
  return (
    <Suspense
      fallback={
        <DelayedFallback>
          <RouteFallback />
        </DelayedFallback>
      }
    >
      <Outlet />
    </Suspense>
  );
}
